import * as Yup from "yup";
import "../accountsInfo.css";
import "react-phone-input-2/lib/style.css";
import { useRef, useState, useEffect } from "react";
import PhoneInput from "react-phone-input-2";
import Toaster from "../../../components/Toast/Toast";
import { useDispatch, useSelector } from "react-redux";
import { toggleReFetch } from "../../../redux/slices/themeSlice";
import {
  getLanguages,
  postUserDetils,
} from "../../../api/modules/b2droppie.class";

const PersonalInfo = () => {
  const dispatch = useDispatch();
  const [errors, setErrors] = useState({});
  const [touched, setTouched] = useState({});
  const [languages, setLanguages] = useState([]);
  const userData = useSelector((state) => state?.auth?.user);
  const [toast, setToast] = useState({ success: "", error: "" });
  const isDarkMode = useSelector((state) => state.theme.darkMode);

  const fieldRefs = {
    firstName: useRef(null),
    lastName: useRef(null),
    username: useRef(null),
    phone: useRef(null),
    language_id: useRef(null),
  };

  const [formData, setFormData] = useState({
    email: "",
    phone: "",
    username: "",
    lastName: "",
    firstName: "",
    language_id: "",
  });

  const [originalData, setOriginalData] = useState({});

  useEffect(() => {
    // fetchLanguages();
    if (userData) {
      const data = {
        email: userData?.email ?? "",
        lastName: userData?.last_name ?? "",
        firstName: userData?.first_name ?? "",
        phone: userData?.userdetails?.phone_number ?? "",
        username: userData?.userdetails?.username ?? userData?.username ?? "",
        language_id:
          userData?.userdetails?.language_id ?? userData?.language?.id ?? "",
      };
      setFormData(data);
      setOriginalData(data);
    }
  }, [userData]);

  const fetchLanguages = async () => {
    try {
      const response = await getLanguages();
      setLanguages(response?.data?.data || []);
    } catch (err) {
      console.error("Error fetching languages:", err);
    }
  };

  const validationSchema = Yup.object().shape({
    firstName: Yup.string()
      .required("First name is required")
      .max(20, "First name cannot exceed 20 characters"),
    lastName: Yup.string()
      .required("Last name is required")
      .max(20, "Last name cannot exceed 20 characters"),
    phone: Yup.string().matches(/^\+?[1-9]\d{7,14}$/, {
      message: "Enter a valid phone number",
      excludeEmptyString: true,
    }),

    email: Yup.string()
      .email("Invalid email address")
      .required("Email is required"),
    username: Yup.string()
      .required("Username is required")
      .max(15, "Username cannot exceed 15 characters"),
    language_id: Yup.string(),
  });

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));

    if (touched[name]) {
      validationSchema
        .validateAt(name, { ...formData, [name]: value })
        .then(() => setErrors((prev) => ({ ...prev, [name]: "" })))
        .catch((err) =>
          setErrors((prev) => ({ ...prev, [name]: err.message })),
        );
    }
  };

  const handlePhoneChange = (value) => {
    const phoneValue = value ? `+${value}` : "";

    setFormData((prev) => ({ ...prev, phone: phoneValue }));

    setTouched((prev) => ({ ...prev, phone: true }));

    validationSchema
      .validateAt("phone", { ...formData, phone: phoneValue })
      .then(() => setErrors((prev) => ({ ...prev, phone: "" })))
      .catch((err) => setErrors((prev) => ({ ...prev, phone: err.message })));
  };

  const handleBlur = (e) => {
    const { name } = e.target;
    setTouched((prev) => ({ ...prev, [name]: true }));

    validationSchema
      .validateAt(name, formData)
      .then(() => setErrors((prev) => ({ ...prev, [name]: "" })))
      .catch((err) => setErrors((prev) => ({ ...prev, [name]: err.message })));
  };

  const isFormChanged = Object.keys(originalData).some(
    (key) => originalData[key] !== formData[key],
  );

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!isFormChanged) {
      setToast({
        success: "",
        error: "There are no changes to update",
      });
      return;
    }

    try {
      await validationSchema.validate(formData, { abortEarly: false });
      setErrors({});

      const data = {
        email: formData.email,
        username: formData.username,
        last_name: formData.lastName,
        phone_number: formData.phone,
        first_name: formData.firstName,
        language_id: parseInt(formData.language_id),
      };

      const response = await postUserDetils(data);

      if (response?.status === 200 || response?.success) {
        dispatch(toggleReFetch("mine"));
        setOriginalData(formData);

        setToast({
          success: response?.data?.message || "Profile updated successfully",
          error: "",
        });
      } else {
        setToast({ success: "", error: "Failed to update profile." });
      }
    } catch (err) {
      if (err.inner) {
        const formattedErrors = {};
        err.inner.forEach((e) => {
          formattedErrors[e.path] = e.message;
        });

        setErrors(formattedErrors);

        if (err.inner) {
          const formattedErrors = {};
          const allTouched = {};

          err.inner.forEach((e) => {
            formattedErrors[e.path] = e.message;
            allTouched[e.path] = true;
          });

          setErrors(formattedErrors);
          setTouched((prev) => ({ ...prev, ...allTouched }));

          const firstErrorField = Object.keys(formattedErrors)[0];
          const ref = fieldRefs[firstErrorField];

          if (ref?.current) {
            ref.current.scrollIntoView({
              behavior: "smooth",
              block: "center",
            });
            ref.current.focus?.();
          }
        }
      } else {
        setToast({
          success: "",
          error: "Something went wrong. Try again.",
        });
      }
    }
  };

  return (
    <>
      <Toaster message={toast} />
      <div className={`user-info-outer ${isDarkMode ? "dark" : "light"}`}>
        <h2 className="title mb-0">Personal Information</h2>

        <form onSubmit={handleSubmit} className="mt-32px">
          <div className="row">
            {/* First Name */}
            <div className="col-md-6 col-12 form-group">
              <label>First Name</label>
              <input
                type="text"
                name="firstName"
                onBlur={handleBlur}
                onChange={handleChange}
                ref={fieldRefs.firstName}
                value={formData.firstName}
                className={`input-field ${
                  errors.firstName && touched.firstName ? "error" : ""
                }`}
              />

              {errors.firstName && touched.firstName && (
                <span className="error-text">{errors.firstName}</span>
              )}
            </div>

            {/* Last Name */}
            <div className="col-md-6 col-12 form-group">
              <label>Last Name</label>
              <input
                type="text"
                name="lastName"
                onBlur={handleBlur}
                onChange={handleChange}
                ref={fieldRefs.lastName}
                value={formData.lastName}
                className={`input-field ${
                  errors.lastName && touched.lastName ? "error" : ""
                }`}
              />

              {errors.lastName && touched.lastName && (
                <span className="error-text">{errors.lastName}</span>
              )}
            </div>

            {/* Username */}
            <div className="col-md-6 col-12 form-group mt-3">
              <label>Username</label>
              <input
                type="text"
                name="username"
                onBlur={handleBlur}
                onChange={handleChange}
                ref={fieldRefs.username}
                value={formData.username}
                className={`input-field ${
                  errors.username && touched.username ? "error" : ""
                }`}
              />

              {errors.username && touched.username && (
                <span className="error-text">{errors.username}</span>
              )}
            </div>

            {/* Email */}
            <div className="col-md-6 col-12 form-group mt-3">
              <label>Email</label>
              <input
                disabled
                type="email"
                value={formData.email}
                className="input-field disabled"
              />
            </div>

            {/* Phone */}
            <div className="col-md-6 col-12 form-group mt-3">
              <label>Phone</label>
              <div ref={fieldRefs.phone}>
                <PhoneInput
                  country={"us"}
                  value={formData.phone}
                  onChange={handlePhoneChange}
                  inputClass={`input-field phone ${
                    errors.phone && touched.phone ? "error" : ""
                  }`}
                  buttonClass="phone-btn"
                />
              </div>
              {errors.phone && touched.phone && (
                <span className="error-text">{errors.phone}</span>
              )}
            </div>

            {/* Language */}
            {/* <div className="col-md-6 col-12 form-group mt-3">
            <label>Language</label>
            <select
              name="language_id"
              onBlur={handleBlur}
              onChange={handleChange}
              ref={fieldRefs.language_id}
              value={formData.language_id}
              className={`input-field ${
                errors.language_id && touched.language_id ? "error" : ""
              }`}
            >
              <option value="">Select Language</option>
              {languages.map((lang) => (
                <option value={lang.id} key={lang.id}>
                  {lang.name}
                </option>
              ))}
            </select>
            {errors.language_id && touched.language_id && (
              <span className="error-text">{errors.language_id}</span>
            )}
          </div> */}

            {/* Buttons */}
            <div className="d-end gap-10px my-auto mt-4">
              <button
                type="submit"
                className="filled-medium-deep-yellow-btn-dark w-lg-20 w-md-50"
              >
                Update
              </button>
            </div>
          </div>
        </form>
      </div>
    </>
  );
};

export default PersonalInfo;
